/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 *
 */

/**
 * Singleton pattern, time is in milliseconds
 * http://blog.mgechev.com/2014/04/16/singleton-in-javascript/
 *
 * Usage:
 *  Basic usage
 *  WebglTimer.getInstance().start(...)
 *  WebglTimer.getInstance().get(...)
 *  WebglTimer.getInstance().stop(...)
 *
 *  Synchronous usage
 *  WebglTimer.getInstance().measure(..., function (time) {
 *      cadwork.webgl.Debug.logInfo(time);
 *  });
 *
 *  Asynchronous usage
 *  measure = true;
 *  while (measure) {   // This you can imagine as a render loop and boolean inside it
 *    WebglTimer.getInstance().measure2("name", sheduleRedraw, 1000, function (n, t) {
 *      cadwork.webgl.Debug.logInfo(t);
 *      WebglTimer.getInstance().stop(n);   // Clear measurement
 *      measure = false;                    // Stop measurement, if not stopped, it will start again from zero
 *    });
 *  }
 */

export class WebglTimer{
    private static _instance: WebglTimer = null;
    private _measures: {} = {};

    constructor() {
    }

    public static getInstance(): WebglTimer {
        if (!WebglTimer._instance) {
            WebglTimer._instance = new WebglTimer();
        }
        return WebglTimer._instance;
    }

    /**
     * Start measurement
     * @param name name of measurement
     * @returns {{time: number, num: number}}
     */
    public start(name: string): object {
        let obj = {
            time: Date.now(),
            num: 0
        };

        this._measures[name] = obj;
        return obj;
    }

    /**
     * Get current object of measurement
     * @param name name of measurement
     * @returns {{time: number, num: number} | undefined}
     */
    public get(name: string): object {
        return this._measures[name];
    }

    /**
     * Get current time of measurement
     * @param name name of measurement
     * @returns {number | null}
     */
    public split(name: string): number {
        if (this._measures[name] === undefined) {
            return null;
        }

        return Date.now() - this._measures[name].time;
    }

    /**
     * Stop measurement and get time from start
     * @param name name of measurement
     * @returns {number|null}
     */
    public stop(name: string): number {
        let time = this.split(name);
        if (time !== null) {
            delete this._measures[name];
        }
        return time;
    }

    /**
     * Start measurement with specific function and count of iterations, at the end call callback function
     * and return the time.
     *
     * @param fce function to call every tick
     * @param count num of calls of this function
     * @param cb callback function with one parameter (time)
     *
     * @returns {number|null}
     */
    public measure(fce: any, count: number, cb: any): number {

        let start = Date.now();

        for (var loop = 0; loop < count; ++loop) {
            fce();
        }

        let time = Date.now() - start;
        cb(time);
        return time;
    }

    /**
     * Start measurement with specific function and count of iterations, at the end call callback function
     * and return the time.
     *
     * @param name name of the measurement
     * @param fce function to call every tick
     * @param count num of calls of this function
     * @param cb callback function with two parameters (name, time)
     */
    public measure2(name: string, fce: any, count: number, cb: any) {

        let last: any = this.get(name);
        if (last === undefined) {
            last = this.start(name);
        }

        if (last.num === count) {
            let time = Date.now() - last.time;
            cb(name, time);
            last.num = count + 1;
        }
        else if (last.num < count) {
            fce();
            ++last.num;
        }
    }
}
